import { useRef, type MouseEvent as ReactMouseEvent } from 'react';
import type { ExcelChart } from '@/lib/documents';

interface ExcelChartCardProps {
  chart: ExcelChart;
  onMove: (id: string, x: number, y: number) => void;
  onRemove: (id: string) => void;
  onResize: (id: string, w: number, h: number) => void;
}

const PALETTE = ['#0f766e', '#ca8a04', '#b45309', '#7c2d12', '#4d7c0f', '#9f1239', '#1e3a8a', '#0e7490'];

/**
 * Carte de graphique affichée par-dessus la feuille de calcul.
 * Déplaçable par son en-tête et redimensionnable par le coin inférieur droit.
 */
export default function ExcelChartCard({ chart, onMove, onRemove, onResize }: ExcelChartCardProps) {
  const dragRef = useRef<{ startX: number; startY: number; x: number; y: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; w: number; h: number } | null>(null);

  const startDrag = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { startX: e.clientX, startY: e.clientY, x: chart.x, y: chart.y };
    const onMoveEvent = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      onMove(chart.id, Math.max(0, d.x + ev.clientX - d.startX), Math.max(0, d.y + ev.clientY - d.startY));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMoveEvent);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMoveEvent);
    window.addEventListener('mouseup', onUp);
  };

  const startResize = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, w: chart.w, h: chart.h };
    const onMoveEvent = (ev: MouseEvent) => {
      const d = resizeRef.current;
      if (!d) return;
      onResize(
        chart.id,
        Math.max(220, d.w + ev.clientX - d.startX),
        Math.max(150, d.h + ev.clientY - d.startY),
      );
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener('mousemove', onMoveEvent);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMoveEvent);
    window.addEventListener('mouseup', onUp);
  };

  const values = chart.series[0]?.values ?? [];
  const max = Math.max(...values, 1);
  const total = values.reduce((a, b) => a + b, 0) || 1;
  let acc = 0;
  const pieStops = values
    .map((v, i) => {
      const start = (acc / total) * 100;
      acc += v;
      const end = (acc / total) * 100;
      return `${PALETTE[i % PALETTE.length]} ${start}% ${end}%`;
    })
    .join(', ');

  return (
    <div
      className="absolute z-30 bg-white border border-background-200/70 rounded-lg overflow-hidden"
      style={{ left: chart.x, top: chart.y, width: chart.w, height: chart.h }}
    >
      <div
        onMouseDown={startDrag}
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-background-50 border-b border-background-200/70 cursor-move select-none"
      >
        <i className="ri-drag-move-2-line text-foreground-400 text-xs"></i>
        <p className="flex-1 text-[11px] font-semibold text-foreground-800 truncate">{chart.title}</p>
        <button
          onClick={() => onRemove(chart.id)}
          className="w-5 h-5 rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center cursor-pointer"
          title="Supprimer le graphique"
        >
          <i className="ri-close-line text-xs"></i>
        </button>
      </div>

      <div className="p-2.5 h-[calc(100%-30px)] overflow-hidden">
        {chart.kind === 'pie' ? (
          <div className="flex items-center gap-3 h-full">
            <div className="w-20 h-20 rounded-full flex-shrink-0" style={{ background: `conic-gradient(${pieStops})` }}></div>
            <div className="flex-1 min-w-0 space-y-1 overflow-y-auto">
              {chart.categories.slice(0, 8).map((c, i) => (
                <div key={`${c}-${i}`} className="flex items-center gap-1.5 text-[10px] text-foreground-600 min-w-0">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }}></span>
                  <span className="truncate">{c}</span>
                  <span className="ml-auto text-foreground-400">{values[i] ?? ''}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-end gap-1 h-full">
            {values.slice(0, 14).map((v, i) => (
              <div key={`${chart.categories[i] ?? i}-${i}`} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
                <span className="text-[8px] text-foreground-500 mb-0.5">{v}</span>
                <div
                  className={`w-full rounded-t ${chart.kind === 'line' ? 'bg-accent-400/70' : 'bg-accent-500/80'}`}
                  style={{ height: `${Math.max(3, (v / max) * 100)}%` }}
                  title={`${chart.categories[i] ?? i} : ${v}`}
                ></div>
                <span className="mt-0.5 w-full text-[8px] text-foreground-400 truncate text-center">
                  {chart.categories[i] ?? i}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        onMouseDown={startResize}
        className="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize flex items-end justify-end text-foreground-300"
        title="Redimensionner"
      >
        <i className="ri-corner-right-down-line text-[10px]"></i>
      </div>
    </div>
  );
}